import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { apiClient } from '../api/client';
import { Button } from '../components/ui/Button';
import { QuestionCard } from '../components/ui/QuestionCard';
import { Card, CardContent } from '../components/ui/Card';
import { ArrowLeft, ArrowRight, CheckCircle, Brain } from 'lucide-react';
import ProctorCamera from '../components/ProctorCamera';
import useEnterpriseProctoring from '../hooks/useEnterpriseProctoring';
import ProctoringAlertBanner from '../components/assessment/ProctoringAlertBanner';
import ProctoringStatusPanel from '../components/assessment/ProctoringStatusPanel';
import { stopAllCameraStreams } from '../utils/cameraUtils';
import toast from 'react-hot-toast';

const BigFiveTest = () => {
  const [testData, setTestData] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [questionStartTime, setQuestionStartTime] = useState(Date.now());
  const [timeTaken, setTimeTaken] = useState({});
  // MODIFIED: photo capture moved to SharedVerifyPage at verify route.
  const [profilePhoto, setProfilePhoto] = useState(() => sessionStorage.getItem('bigfive_productivity_verify_photo'));
  const [lastAnswerTimestamp, setLastAnswerTimestamp] = useState(Date.now());
  const videoRef = useRef(null);
  const testStreamRef = useRef(null);
  const forceSubmitRef = useRef(() => {});
  const { user, token } = useAuth();
  const navigate = useNavigate();

  // Auto-save key
  const STORAGE_KEY = `bigfive_progress_${user?.uid}`;

  // ADDED: enterprise proctoring for unified monitoring across assessments.
  const {
    proctoringReport,
    currentAlert,
    dismissAlert,
    violations,
    integrityScore,
    faceStatus,
    gazeStatus,
    isFullscreen,
    requestFullscreen,
    recordAnswer
  } = useEnterpriseProctoring({
    assessmentKey: 'bigfive_productivity',
    videoRef,
    enabled: true,
    onViolation: (type, detail) => {
      console.warn('[Proctoring]', type, detail);
    },
    onForcedSubmit: () => forceSubmitRef.current?.(),
    maxViolations: 10
  });

  // Load saved progress on mount
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const { answers: savedAnswers, currentQuestion: savedQuestion } = JSON.parse(saved);
        setAnswers(savedAnswers || {});
        setCurrentQuestion(savedQuestion || 0);
        toast.success('Progress restored');
      } catch (e) {
        console.error('Failed to restore progress:', e);
      }
    }
  }, [user]);

  // Auto-save progress whenever answers change
  useEffect(() => {
    if (Object.keys(answers).length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ answers, currentQuestion }));
    }
  }, [answers, currentQuestion]);

  useEffect(() => {
    // MODIFIED: enforce verify route before test start.
    if (!profilePhoto) {
      navigate('/assessment/bigfive_productivity/verify');
    }
  }, [navigate, profilePhoto]);

  useEffect(() => {
    // ADDED: hidden webcam stream for enterprise proctoring.
    navigator.mediaDevices
      .getUserMedia({ video: true, audio: false })
      .then((stream) => {
        testStreamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      })
      .catch(() => {
        // Proctoring degrades gracefully if camera stream cannot be started here.
      });

    return () => {
      if (testStreamRef.current) {
        testStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const startTest = useCallback(async () => {
    console.log('🚀 BigFiveTest: startTest called', { cameraActive, loading, profilePhoto });
    if (!cameraActive || !profilePhoto) {
      console.log('⏸️ BigFiveTest: Waiting for camera and profile photo...');
      setLoading(false);
      return;
    }
    
    try {
      console.log('📝 BigFiveTest: Fetching test data...');
      const response = await apiClient.post('/test/bigfive/start', {
        profilePhoto
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('✅ BigFiveTest: Test data received', response.data);
      setTestData(response.data);
    } catch (error) {
      console.error('❌ BigFiveTest: Failed to start test:', error);
      const errorMsg = error.response?.data?.error || 'Failed to start test';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [token, cameraActive, profilePhoto]);

  useEffect(() => {
    console.log('🔄 BigFiveTest: Effect triggered', { user: !!user, token: !!token, cameraActive, profilePhoto: !!profilePhoto });
    if (user && token && cameraActive && profilePhoto) {
      console.log('▶️ BigFiveTest: Starting test...');
      startTest();
    }
  }, [user, token, cameraActive, profilePhoto, startTest]);

  // Handle camera loss - only redirect if test has actually started
  const handleCameraLost = (reason) => {
    console.log('🚨 BigFiveTest: handleCameraLost called', { reason, testData: !!testData, loading });
    // Don't redirect if test hasn't loaded yet or is still loading
    if (testData && !loading) {
      console.log('⚠️ BigFiveTest: Redirecting in 2 seconds...');
      setTimeout(() => {
        toast.error('Assessment terminated: Camera access required');
        navigate('/assessment');
      }, 2000);
    } else {
      console.log('✅ BigFiveTest: Ignoring camera lost (test not started yet)');
    }
  };

  // Prevent test if camera lost during test
  useEffect(() => {
    console.log('🔍 BigFiveTest: Camera monitor effect', { testData: !!testData, loading, cameraActive });
    if (testData && !loading && !cameraActive) {
      console.log('⚠️ BigFiveTest: Camera lost during test, redirecting in 5s...');
      const timer = setTimeout(() => {
        toast.error('Camera access is required for proctored assessment');
        navigate('/assessment');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [cameraActive, testData, loading, navigate]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (e.key === 'Enter' && answers.hasOwnProperty(currentQuestion)) {
        nextQuestion();
      }
    };
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [currentQuestion, answers]);

  const handleAnswer = (value) => {
    const timeSpent = Math.floor((Date.now() - questionStartTime) / 1000);
    setAnswers(prev => ({
      ...prev,
      [currentQuestion]: value
    }));
    setTimeTaken(prev => ({
      ...prev,
      [currentQuestion]: timeSpent
    }));
    
    // ADDED: enterprise proctoring rapid-answer capture.
    recordAnswer(Date.now() - lastAnswerTimestamp);
    setLastAnswerTimestamp(Date.now());
  };

  const nextQuestion = () => {
    if (testData?.questions && currentQuestion < testData.questions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
      setQuestionStartTime(Date.now());
    } else {
      submitTest();
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
      setQuestionStartTime(Date.now());
    }
  };

  const submitTest = async () => {
    setSubmitting(true);
    try {
      console.log('📤 BigFiveTest: Submitting test...');
      console.log('📸 BigFiveTest: Profile photo in state:', profilePhoto ? `${profilePhoto.length} chars` : 'NULL');
      
      // Convert answers object to array format for backend
      const answersArray = [];
      if (testData?.questions) {
        for (let i = 0; i < testData.questions.length; i++) {
          answersArray[i] = answers[i];
        }
      }
      
      const payload = {
        answers: answersArray,
        profilePhoto,
        // MODIFIED: proctoringData now from useEnterpriseProctoring hook.
        proctoringReport,
        deviceInfo: {
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString()
        },
        sessionData: {
          startTime: testData.startTime || new Date().toISOString(),
          completedAt: new Date().toISOString()
        }
      };
      
      console.log('📦 BigFiveTest: Payload prepared, profilePhoto included:', !!payload.profilePhoto);
      
      const response = await apiClient.post('/tests/bigfive/submit', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ BigFiveTest: Submit successful:', response.data);
      
      // Clear saved progress after successful submission
      localStorage.removeItem(STORAGE_KEY);
      
      // Stop current camera before transitioning
      stopAllCameraStreams();
      
      toast.success('Section 1 completed! Moving to Productivity Assessment...');
      setTimeout(() => {
        navigate('/test/productivity');
      }, 1500);
    } catch (error) {
      console.error('Failed to submit test:', error);
      toast.error('Failed to submit test');
    } finally {
      setSubmitting(false);
    }
  };

  const getCurrentDomain = () => {
    if (!testData?.questions[currentQuestion]) return null;
    return testData.questions[currentQuestion].domain;
  };

  const progress = testData?.questions ? Math.round(((currentQuestion + 1) / testData.questions.length) * 100) : 0;
  forceSubmitRef.current = () => {
    if (!submitting) {
      submitTest();
    }
  };

  if (loading || !cameraActive || !profilePhoto) {
    return (
      <>
        <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-blue-900 flex items-center justify-center relative overflow-hidden">
        {/* Animated Background Circles */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
            animate={{ scale: [1, 1.2, 1], x: [0, 50, 0], y: [0, 30, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-purple-500/20 rounded-full blur-3xl"
            animate={{ scale: [1.2, 1, 1.2], x: [0, -50, 0], y: [0, -30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <ProctorCamera 
          onCameraStatus={setCameraActive} 
          onCameraLost={handleCameraLost}
        />
        
        <motion.div 
          className="text-center z-10 max-w-2xl px-4"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Brain Icon with Pulse */}
          <motion.div
            className="relative mb-8 inline-block"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <div className="absolute inset-0 bg-blue-400/30 rounded-full blur-2xl"></div>
            <div className="relative bg-gradient-to-br from-blue-400 to-purple-500 p-8 rounded-3xl shadow-2xl">
              <Brain className="w-20 h-20 text-white" />
            </div>
          </motion.div>

          {/* Title */}
          <motion.h2 
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            BIG FIVE Assessment
          </motion.h2>
          
          {/* Subtitle */}
          <motion.p 
            className="text-xl text-blue-100 mb-8"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            {!cameraActive ? 'Initializing proctoring system...' : 'Preparing your personality analysis...'}
          </motion.p>

          {/* Loading Dots */}
          <div className="flex justify-center space-x-3 mb-8">
            {[0, 1, 2].map((i) => (
              <motion.div
                key={i}
                className="w-4 h-4 bg-blue-400 rounded-full"
                animate={{ y: [0, -20, 0], opacity: [0.5, 1, 0.5] }}
                transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
              />
            ))}
          </div>

          {/* Camera Warning */}
          {!cameraActive && (
            <motion.div
              className="bg-yellow-500/10 backdrop-blur-sm border border-yellow-400/30 rounded-2xl p-6 max-w-md mx-auto"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-yellow-400/20 rounded-full flex items-center justify-center">
                    <span className="text-2xl">📹</span>
                  </div>
                </div>
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-yellow-300 mb-2">Camera Access Required</h3>
                  <p className="text-sm text-yellow-100">
                    Please enable camera permissions to start the proctored assessment.
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {!profilePhoto && cameraActive ? (
            <p className="text-blue-100 mt-4 text-sm">Waiting for verification photo...</p>
          ) : null}

          {/* Features */}
          {cameraActive && (
            <motion.div
              className="grid grid-cols-3 gap-4 mt-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
            >
              {[
                { icon: '🎯', label: '50 Questions' },
                { icon: '⏱️', label: '~15 Minutes' },
                { icon: '🔒', label: 'Secure & Private' }
              ].map((item, i) => (
                <motion.div
                  key={i}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                  whileHover={{ scale: 1.05 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="text-3xl mb-2">{item.icon}</div>
                  <div className="text-sm text-blue-100">{item.label}</div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </motion.div>
      </div>
      </>
    );
  }

  if (!testData) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Failed to load test. Please try again.</p>
        <button
          onClick={() => navigate('/dashboard')}
          className="mt-4 text-violet-600 hover:text-violet-700 font-semibold"
        >
          Return to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      <video
        ref={videoRef}
        autoPlay
        muted
        playsInline
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        aria-hidden="true"
      />
      <ProctoringAlertBanner message={currentAlert} onDismiss={dismissAlert} />
      <ProctoringStatusPanel
        faceStatus={faceStatus}
        gazeStatus={gazeStatus}
        violations={violations}
        integrityScore={integrityScore}
        isFullscreen={isFullscreen}
        onRequestFullscreen={requestFullscreen}
        videoRef={videoRef}
      />
      {/* Proctoring Components */}
      <ProctorCamera 
        onCameraStatus={setCameraActive} 
        onCameraLost={handleCameraLost}
      />
      
      <div className="max-w-4xl mx-auto px-4 flex-1 flex flex-col justify-center">
        {/* Header */}
        <motion.div 
          className="text-center mb-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="flex items-center justify-center mb-2">
            <Brain className="w-6 h-6 text-blue-600 mr-2" />
            <h1 className="text-2xl font-bold text-gray-900">
              BIG FIVE Assessment
            </h1>
          </div>
          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <span>Question {currentQuestion + 1} of {testData?.questions?.length || 0}</span>
            {getCurrentDomain() && (
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                {getCurrentDomain()}
              </span>
            )}
          </div>
        </motion.div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          {testData?.questions?.[currentQuestion] && (
            <QuestionCard
              key={currentQuestion}
              question={testData.questions[currentQuestion]}
              currentQuestion={currentQuestion + 1}
              totalQuestions={testData.questions.length}
              selectedAnswer={answers[currentQuestion]}
              onAnswerSelect={handleAnswer}
            />
          )}
        </AnimatePresence>

        {/* Navigation */}
        <motion.div 
          className="flex justify-between items-center mt-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <Button
            onClick={previousQuestion}
            disabled={currentQuestion === 0}
            variant="outline"
            size="lg"
            className="flex items-center space-x-2"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Previous</span>
          </Button>
          
          {testData?.questions && currentQuestion === testData.questions.length - 1 ? (
            <Button
              onClick={nextQuestion}
              disabled={!answers.hasOwnProperty(currentQuestion) || submitting}
              loading={submitting}
              variant="success"
              size="lg"
              className="flex items-center space-x-2"
            >
              <CheckCircle className="w-4 h-4" />
              <span>{submitting ? 'Submitting...' : 'Complete Assessment'}</span>
            </Button>
          ) : (
            <Button
              onClick={nextQuestion}
              disabled={!answers.hasOwnProperty(currentQuestion)}
              variant="default"
              size="lg"
              className="flex items-center space-x-2"
            >
              <span>Next</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          )}
        </motion.div>


      </div>
    </div>
  );
};

export default BigFiveTest;
